import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/client/lib/utils';

/**
 * Renders raw markdown text as styled HTML.
 * Uses the same visual style as the chat markdown (aui-md classes).
 */
export function MarkdownContent({ content }: { content: string }) {
  return (
    <div className="aui-md prose prose-sm max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        children={content}
        components={{
          h1: ({ className, ...props }) => (
            <h1
              className={cn(
                'mb-3 scroll-m-20 text-xl font-semibold first:mt-0 last:mb-0',
                className,
              )}
              {...props}
            />
          ),
          h2: ({ className, ...props }) => (
            <h2
              className={cn(
                'mt-6 mb-2 scroll-m-20 text-lg font-semibold first:mt-0 last:mb-0',
                className,
              )}
              {...props}
            />
          ),
          h3: ({ className, ...props }) => (
            <h3
              className={cn(
                'mt-4 mb-1.5 scroll-m-20 text-base font-semibold first:mt-0 last:mb-0',
                className,
              )}
              {...props}
            />
          ),
          h4: ({ className, ...props }) => (
            <h4
              className={cn(
                'mt-3 mb-1 scroll-m-20 text-sm font-medium first:mt-0 last:mb-0',
                className,
              )}
              {...props}
            />
          ),
          p: ({ className, ...props }) => (
            <p
              className={cn('my-2.5 leading-relaxed first:mt-0 last:mb-0', className)}
              {...props}
            />
          ),
          a: ({ className, ...props }) => (
            <a
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'text-primary underline underline-offset-2 hover:text-primary/80',
                className,
              )}
              {...props}
            />
          ),
          blockquote: ({ className, ...props }) => (
            <blockquote
              className={cn(
                'my-2.5 border-muted-foreground/30 border-l-2 pl-3 text-muted-foreground italic',
                className,
              )}
              {...props}
            />
          ),
          ul: ({ className, ...props }) => (
            <ul
              className={cn(
                'my-2 ml-4 list-disc marker:text-muted-foreground [&>li]:mt-1',
                className,
              )}
              {...props}
            />
          ),
          ol: ({ className, ...props }) => (
            <ol
              className={cn(
                'my-2 ml-4 list-decimal marker:text-muted-foreground [&>li]:mt-1',
                className,
              )}
              {...props}
            />
          ),
          li: ({ className, ...props }) => (
            <li className={cn('leading-relaxed', className)} {...props} />
          ),
          hr: ({ className, ...props }) => (
            <hr className={cn('my-4 border-muted-foreground/20', className)} {...props} />
          ),
          table: ({ className, ...props }) => (
            <div className="my-3 overflow-x-auto">
              <table
                className={cn('w-full border-separate border-spacing-0', className)}
                {...props}
              />
            </div>
          ),
          th: ({ className, ...props }) => (
            <th
              className={cn(
                'bg-muted px-3 py-1.5 text-left text-sm font-medium first:rounded-tl-lg last:rounded-tr-lg',
                className,
              )}
              {...props}
            />
          ),
          td: ({ className, ...props }) => (
            <td
              className={cn(
                'border-muted-foreground/20 border-b border-l px-3 py-1.5 text-sm last:border-r',
                className,
              )}
              {...props}
            />
          ),
          tr: ({ className, ...props }) => (
            <tr
              className={cn(
                'm-0 border-b p-0 first:border-t [&:last-child>td:first-child]:rounded-bl-lg [&:last-child>td:last-child]:rounded-br-lg',
                className,
              )}
              {...props}
            />
          ),
          pre: ({ className, ...props }) => (
            <pre
              className={cn(
                'overflow-x-auto rounded-lg border border-border/50 bg-muted/30 p-3 text-xs leading-relaxed my-3',
                className,
              )}
              {...props}
            />
          ),
          code: ({ className, ...props }) => {
            const isBlock = className?.includes('language-');
            if (isBlock) return <code className={className} {...props} />;
            return (
              <code
                className={cn(
                  'rounded-md border border-border/50 bg-muted/50 px-1.5 py-0.5 font-mono text-[0.85em]',
                  className,
                )}
                {...props}
              />
            );
          },
          img: ({ className, ...props }) => (
            <img className={cn('my-3 max-w-full rounded-md', className)} {...props} />
          ),
        }}
      />
    </div>
  );
}
